import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from "next";
import React from "react";
import { initializeApollo } from "../../lib/apolloClient";
import { NewsListingFragment } from "./../../__generated__/graphql";
import { getNewsPage } from "./../../api/graphql/queries";
import NewsListing from "./../../cmsPages/NewsListing";
import Error404 from "./../../errorPages/Error404";
import Error500 from "./../../errorPages/Error500";
import { getFrontendHostname, getTheme } from './../../lib/config';

type NewsPageProps = {
  data: {
    news: NewsListingFragment
  };
  pageUrl: string;
  siteName: string;
  homepageUrl: string;
  errorCode?: number;
};

const NewsPage: NextPage<NewsPageProps> = (props: NewsPageProps) => {

  if (props.errorCode === 500) {
    return <Error500 />;
  }
  else if (props.errorCode) {
    return <Error404 />;
  }
  return <NewsListing type="news" pageUrl={props.pageUrl} {...props.data.news} />;
};

export default NewsPage;

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const { res, req } = context;
  const theme = getTheme();
  const path = context.resolvedUrl;
  const hostname = getFrontendHostname();

  let pageUrl;
  if (req) {
    // Server side rendering
    pageUrl = 'https://' + hostname + req.url;
  } else {
    // Client side rendering
    pageUrl = window.location.href;
  }
  const url = new URL(pageUrl);
  // ZSL and Zoos use different URLs to reach this page. Only allow the correct URL so we don't create duplicate content.
  if ((theme === 'zsl' && url.pathname.includes('zoo-stories'))
    || (theme !== 'zsl' && url.pathname.includes('news-and-events'))) {
    res.statusCode = 404;
    return {
      props: {
        theme: theme,
        pageUrl: pageUrl,
        hostname: hostname,
        errorCode: 404
      }
    };
  }

  // Get current params for the query.
  const params = url.searchParams;
  // Page number
  const page = (Number.parseInt(params.get('page') || '0|') - 1) || 0;
  // Tag filters.
  const dept = params.getAll('news_department');
  const flatDept = dept ? dept.map((value) => value.split(',')).flat() : null;

  const apolloClient = initializeApollo();
  const result = await apolloClient.query({
    query: getNewsPage,
    variables: {
      type: "news",
      page: Math.max(page, 0),
      filterDept: flatDept || null,
    }
  })
  .catch((e) => {
    console.error('[GraphQL] News query failed:', e);
    res.statusCode = 500;
    return {
      data: {
        route:{}
      },
      errorCode: 500
    };
  });

  return {
    props: {
      theme: theme,
      pageUrl: pageUrl,
      hostname: hostname,
      ...result
    },
  };
};
